import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { isProductionManagerRole } from '../../../lib/operatorRoles';

const AlertSystemPanel = ({ alerts, batches, onBatchSelect, userRole, currentUser }) => {
  // Filter batches and alerts based on user role
  const visibleBatches = useMemo(() => {
    if (!batches) return [];
    
    if (isProductionManagerRole(userRole) && currentUser?.id) {
      return batches.filter(batch => 
        batch.responsible === currentUser.id || 
        batch.responsibleId === currentUser.id
      );
    }
    
    return batches;
  }, [batches, userRole, currentUser]);

  // Filter alerts to only show those for visible batches
  const visibleAlerts = useMemo(() => {
    if (!alerts) return [];
    
    if (isProductionManagerRole(userRole) && currentUser?.id) {
      const visibleBatchIds = visibleBatches.map(b => b.id);
      return alerts.filter(alert => visibleBatchIds.includes(alert.batchId));
    }
    
    return alerts;
  }, [alerts, visibleBatches, userRole, currentUser]);

  const getAlertConfig = (type) => {
    const configs = {
      error: { bgColor: 'bg-error/10', borderColor: 'border-error/30', textColor: 'text-error', icon: 'AlertCircle' },
      warning: { bgColor: 'bg-warning/10', borderColor: 'border-warning/30', textColor: 'text-warning', icon: 'AlertTriangle' },
      info: { bgColor: 'bg-primary/10', borderColor: 'border-primary/30', textColor: 'text-primary', icon: 'Info' }
    };
    return configs?.[type] || configs?.info;
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000);
    
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    return `Il y a ${Math.floor(diff / 86400)} j`;
  };

  const handleAlertClick = (alert) => {
    const batch = visibleBatches?.find(b => b?.id === alert?.batchId);
    if (batch) {
      onBatchSelect(batch);
    }
  };

  const criticalAlerts = visibleAlerts?.filter(a => a?.type === 'error');
  const warningAlerts = visibleAlerts?.filter(a => a?.type === 'warning');
  const infoAlerts = visibleAlerts?.filter(a => a?.type === 'info');

  return (
    <div className="space-y-6">
      {/* User Role Notice */}
      {isProductionManagerRole(userRole) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Icon name="Info" size={16} />
            <span className="font-medium">Affichage de vos alertes uniquement</span>
          </div>
        </div>
      )}

      {/* Alert Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
              <Icon name="AlertCircle" size={24} className="text-error" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{criticalAlerts?.length}</div>
              <div className="text-sm text-gray-600">Alertes critiques</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{warningAlerts?.length}</div>
              <div className="text-sm text-gray-600">Avertissements</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Info" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{infoAlerts?.length}</div>
              <div className="text-sm text-gray-600">Informations</div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert List */}
      {visibleAlerts?.length > 0 ? (
        <div className="space-y-3">
          {visibleAlerts?.map(alert => {
            const config = getAlertConfig(alert?.type);
            return (
              <div
                key={alert?.id}
                className={`${config?.bgColor} border ${config?.borderColor} rounded-lg p-5 cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => handleAlertClick(alert)}
              >
                <div className="flex items-start gap-4">
                  <Icon name={config?.icon} size={24} className={config?.textColor} />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className={`text-base font-semibold ${config?.textColor}`}>{alert?.title}</h4>
                      <span className="text-xs text-gray-600">{formatTime(alert?.timestamp)}</span>
                    </div>
                    <p className="text-sm text-gray-900 mb-2">{alert?.message}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-medium text-gray-700">Lot: {alert?.batchNumber}</span>
                      <Button variant="ghost" iconName="ArrowRight" size="sm">
                        Voir le lot
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Icon name="CheckCircle" size={64} className="mx-auto text-success mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune alerte active</h3>
          <p className="text-sm text-gray-600">
            {isProductionManagerRole(userRole)
              ? 'Tous vos lots sont dans les délais'
              : 'Tous les lots sont dans les délais'}
          </p>
        </div>
      )}

      {/* Alert Configuration Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration des alertes</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="Clock" size={20} className="text-warning mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Alerte 24 heures</div>
              <div className="text-sm text-gray-600">
                Déclenchée lorsqu'un lot dépasse 24h sans traitement initial
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Calendar" size={20} className="text-error mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Alerte 10 jours</div>
              <div className="text-sm text-gray-600">
                Déclenchée lorsque l'échéance de confirmation de maquette approche (10 jours)
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Bell" size={20} className="text-primary mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Notifications automatiques</div>
              <div className="text-sm text-gray-600">
                Les alertes sont vérifiées automatiquement toutes les 5 minutes
              </div>
            </div>
          </div>
          {isProductionManagerRole(userRole) && (
            <div className="flex items-start gap-3 pt-3 border-t border-gray-200">
              <Icon name="User" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Filtrage par responsable</div>
                <div className="text-sm text-gray-600">
                  Seules les alertes concernant vos lots sont affichées
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertSystemPanel;